import React, { useState, useEffect } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';

const CategorySelector = ({ onCategoryChange, onSubCategoryChange, selectedCategory, selectedSubCategory }) => {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingSubCategories, setIsLoadingSubCategories] = useState(false);
  const [error, setError] = useState(null);
  const [showCategories, setShowCategories] = useState(false);
  const [showSubCategories, setShowSubCategories] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (selectedCategory) {
      fetchSubCategories(selectedCategory.id);
    } else {
      setSubCategories([]);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    setIsLoadingCategories(true);
    setError(null);
    
    try {
      const response = await fetch(`${apiUrl}authentication/categories/`);
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      const data = await response.json();
      setCategories(data);
    } catch (err) {
      setError('Error loading categories. Please try again.');
      console.error('Error fetching categories:', err);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const fetchSubCategories = async (categoryId) => {
    setIsLoadingSubCategories(true);
    setError(null);
    
    try {
      const response = await fetch(`${apiUrl}authentication/categories/${categoryId}/subcategories/`);
      if (!response.ok) {
        throw new Error('Failed to fetch subcategories');
      }
      const data = await response.json();
      setSubCategories(data);
    } catch (err) {
      setError('Error loading subcategories. Please try again.');
      console.error('Error fetching subcategories:', err);
    } finally {
      setIsLoadingSubCategories(false);
    }
  };

  const handleCategorySelect = (category) => {
    onCategoryChange(category);
    onSubCategoryChange(null); // Reset subcategory when category changes
    setShowCategories(false);
  };

  const handleSubCategorySelect = (subCategory) => {
    onSubCategoryChange(subCategory);
    setShowSubCategories(false);
  };

  const getSelectedCategoryName = () => {
    return selectedCategory ? selectedCategory.name : 'Select Category';
  };

  const getSelectedSubCategoryName = () => {
    return selectedSubCategory ? selectedSubCategory.name : 'Select Subcategory';
  };

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-600 text-sm">{error}</p>
        <button 
          onClick={fetchCategories}
          className="mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Category Dropdown */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category *
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowCategories(!showCategories)}
            disabled={isLoadingCategories}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between disabled:opacity-50"
          >
            <span className={selectedCategory ? 'text-gray-900' : 'text-gray-500'}>
              {getSelectedCategoryName()}
            </span>
            {isLoadingCategories ? (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            ) : (
              <ChevronDown className={`h-4 w-4 text-gray-400 transform transition-transform ${showCategories ? 'rotate-180' : ''}`} />
            )}
          </button>
          
          {showCategories && !isLoadingCategories && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {categories.length === 0 ? (
                <div className="px-3 py-2 text-gray-500 text-sm">No categories available</div>
              ) : (
                categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleCategorySelect(category)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none text-gray-900 block"
                  >
                    <div className="flex items-center justify-between">
                      <span>{category.name}</span>
                      <span className="text-xs text-gray-500">
                        {category.subcategories_count} subcategories
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Subcategory Dropdown */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Subcategory *
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowSubCategories(!showSubCategories)}
            disabled={!selectedCategory || isLoadingSubCategories}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className={selectedSubCategory ? 'text-gray-900' : 'text-gray-500'}>
              {!selectedCategory ? 'Select category first' : getSelectedSubCategoryName()}
            </span>
            {isLoadingSubCategories ? (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            ) : (
              <ChevronDown className={`h-4 w-4 text-gray-400 transform transition-transform ${showSubCategories ? 'rotate-180' : ''}`} />
            )}
          </button>
          
          {showSubCategories && !isLoadingSubCategories && selectedCategory && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {subCategories.length === 0 ? (
                <div className="px-3 py-2 text-gray-500 text-sm">No subcategories available</div>
              ) : (
                subCategories.map((subCategory) => (
                  <button
                    key={subCategory.id}
                    type="button"
                    onClick={() => handleSubCategorySelect(subCategory)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none text-gray-900 block"
                  >
                    {subCategory.name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Selected Values Display (for debugging/verification) */}
      {(selectedCategory || selectedSubCategory) && (
        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Selected:</h4>
          {selectedCategory && (
            <p className="text-sm text-gray-600">
              <span className="font-medium">Category:</span> {selectedCategory.name}
            </p>
          )}
          {selectedSubCategory && (
            <p className="text-sm text-gray-600">
              <span className="font-medium">Subcategory:</span> {selectedSubCategory.name}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Example usage component
const CategorySelectorDemo = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    console.log('Category selected:', category);
  };

  const handleSubCategoryChange = (subCategory) => {
    setSelectedSubCategory(subCategory);
    console.log('Subcategory selected:', subCategory);
  };

  const handleSubmit = () => {
    if (selectedCategory && selectedSubCategory) {
      console.log('Form data:', {
        category: selectedCategory,
        subcategory: selectedSubCategory
      });
      alert(`Selected: ${selectedCategory.name} > ${selectedSubCategory.name}`);
    } else {
      alert('Please select both category and subcategory');
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h2 className="text-xl font-bold text-gray-800 mb-6">Category Selection</h2>
      
      <CategorySelector
        onCategoryChange={handleCategoryChange}
        onSubCategoryChange={handleSubCategoryChange}
        selectedCategory={selectedCategory}
        selectedSubCategory={selectedSubCategory}
      />
      
      <button
        onClick={handleSubmit}
        className="w-full mt-6 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200"
      >
        Submit Selection
      </button>
    </div>
  );
};

export default CategorySelectorDemo;